import React, { Suspense } from "react";
import {
  createRouter as createTanStackRouter,
  createRootRoute,
  createRoute,
} from "@tanstack/react-router";

import { RootLayout } from "./routes/__root";

// Lazy-load route components
const AdminDashboard = React.lazy(() =>
  import("./routes/dashboard").then((m) => ({ default: m.AdminDashboard })),
);
const LoginsList = React.lazy(() =>
  import("./routes/logins/list").then((m) => ({ default: m.LoginsList })),
);
const LoginDetail = React.lazy(() =>
  import("./routes/logins/detail").then((m) => ({ default: m.LoginDetail })),
);
const RoomsList = React.lazy(() =>
  import("./routes/rooms/list").then((m) => ({ default: m.RoomsList })),
);
const RoomDetail = React.lazy(() =>
  import("./routes/rooms/detail").then((m) => ({ default: m.RoomDetail })),
);
const HandsList = React.lazy(() =>
  import("./routes/hands/list").then((m) => ({ default: m.HandsList })),
);
const HandDetail = React.lazy(() =>
  import("./routes/hands/detail").then((m) => ({ default: m.HandDetail })),
);
const PlayersList = React.lazy(() =>
  import("./routes/players/list").then((m) => ({ default: m.PlayersList })),
);
const PlayerDetail = React.lazy(() =>
  import("./routes/players/detail").then((m) => ({ default: m.PlayerDetail })),
);
const RoomEventsList = React.lazy(() =>
  import("./routes/room-events/list").then((m) => ({ default: m.RoomEventsList })),
);
const RoomEventDetail = React.lazy(() =>
  import("./routes/room-events/detail").then((m) => ({ default: m.RoomEventDetail })),
);
const PlayerRelationshipsList = React.lazy(() =>
  import("./routes/player-relationships/list").then((m) => ({
    default: m.PlayerRelationshipsList,
  })),
);
const PlayerRelationshipDetail = React.lazy(() =>
  import("./routes/player-relationships/detail").then((m) => ({
    default: m.PlayerRelationshipDetail,
  })),
);
const ChatMessagesList = React.lazy(() =>
  import("./routes/chat-messages/list").then((m) => ({ default: m.ChatMessagesList })),
);
const ChatMessageDetail = React.lazy(() =>
  import("./routes/chat-messages/detail").then((m) => ({ default: m.ChatMessageDetail })),
);
const UserStatsList = React.lazy(() =>
  import("./routes/user-stats/list").then((m) => ({ default: m.UserStatsList })),
);
const UserStatDetail = React.lazy(() =>
  import("./routes/user-stats/detail").then((m) => ({ default: m.UserStatDetail })),
);
const AdminLoginsList = React.lazy(() =>
  import("./routes/admin-logins/list").then((m) => ({ default: m.AdminLoginsList })),
);
const AdminLoginDetail = React.lazy(() =>
  import("./routes/admin-logins/detail").then((m) => ({ default: m.AdminLoginDetail })),
);
const PlayerRequestsList = React.lazy(() =>
  import("./routes/player-requests/list").then((m) => ({ default: m.PlayerRequestsList })),
);
const PlayerRequestDetail = React.lazy(() =>
  import("./routes/player-requests/detail").then((m) => ({ default: m.PlayerRequestDetail })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <Suspense fallback={null}>
      <AdminDashboard />
    </Suspense>
  ),
});

function resourceRoutes(
  path: string,
  List: React.LazyExoticComponent<React.ComponentType>,
  Detail: React.LazyExoticComponent<React.ComponentType>,
) {
  const listRoute = createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => (
      <Suspense fallback={null}>
        <List />
      </Suspense>
    ),
  });
  const detailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `${path}/$id`,
    component: () => (
      <Suspense fallback={null}>
        <Detail />
      </Suspense>
    ),
  });
  return [listRoute, detailRoute];
}

const routeTree = rootRoute.addChildren([
  indexRoute,
  ...resourceRoutes("/logins", LoginsList, LoginDetail),
  ...resourceRoutes("/rooms", RoomsList, RoomDetail),
  ...resourceRoutes("/hands", HandsList, HandDetail),
  ...resourceRoutes("/players", PlayersList, PlayerDetail),
  ...resourceRoutes("/room-events", RoomEventsList, RoomEventDetail),
  ...resourceRoutes("/player-relationships", PlayerRelationshipsList, PlayerRelationshipDetail),
  ...resourceRoutes("/chat-messages", ChatMessagesList, ChatMessageDetail),
  ...resourceRoutes("/user-stats", UserStatsList, UserStatDetail),
  ...resourceRoutes("/admin-logins", AdminLoginsList, AdminLoginDetail),
  ...resourceRoutes("/player-requests", PlayerRequestsList, PlayerRequestDetail),
]);

export function createRouter() {
  return createTanStackRouter({
    routeTree,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
